import { createContext, useContext } from 'react';

// English / Bangla words for the Board Making Plan page. Paper names, job numbers, client names and the station
// codes (D, B, Bm, C, Cm) stay as they are; only the words around them change.

export const STRINGS = {
    en: {
        title: 'Board Making Plan',
        today: 'Today',
        tomorrow: 'Tomorrow',
        closed: 'Closed',
        open: 'Open',
        noPlanYet: 'No plan yet',
        recentDays: 'Recent days…',
        dayItem: '{date} — {status} ({jobs} jobs, {rolls} rolls)',
        loading: 'Loading…',
        tabPlan: '1. Plan',
        tabClose: '2. Day close',
        loadFail: 'Could not load the page. Was dayClose/schema.sql run?',
        linkConsumption: 'Paper per job report',
        linkBalance: 'Paper balance',

        searchPlaceholder: 'Search: job numbers (7838 7840) or party / buyer / item',
        search: 'Search',
        searching: 'Searching…',
        searchFail: 'Search failed',
        showPending: 'All pending jobs',
        filters: 'Filter',
        fParty: 'Party / buyer',
        fPly: 'Ply (all)',
        fSize: 'Roll size',
        fFrom: 'order date from',
        fTo: 'to',
        fApply: 'Apply',
        fClear: 'clear',
        pendingTitle: 'Jobs with a balance left ({n}, newest first) — tick and add',
        searchTitle: 'Search result ({n})',
        hideList: 'hide',
        showList: 'show',
        colCompany: 'Company',
        colAlreadyMade: 'Already made',
        colLeft: 'Balance',
        doneLabel: 'done',
        noJobFound: 'No approved job found.',
        inPlan: 'In plan',
        addTicked: 'Add ticked lines to {date}',
        adding: 'Adding…',
        cancel: 'Cancel',
        tickOne: 'Tick at least one line',
        addedLines: '{added} line(s) added',
        alreadyIn: ', {n} already in the plan',
        addFail: 'Could not add',
        notSaved: 'Not saved',
        confirmPlan: 'Confirm plan',
        confirmedBy: 'Plan confirmed by {name} · {time}',
        changedTitle: 'Changed somewhere else',
        changedMsg: 'The roll notebook of this day was changed somewhere else (Claude or another screen) after this page loaded it. Reload to see the latest — changes typed here and not saved will be lost.',
        reloadBtn: 'Reload',
        removeTitle: 'Remove from plan?',
        remove: 'Remove',
        removeFail: 'Could not remove',
        printSheet: 'Print sheet',
        downloadExcel: 'Download Excel',
        allowPopups: 'Allow pop-ups to print',
        excelFail: 'Could not make the Excel file',
        linesPlanned: '{lines} line(s) · {pcs} pcs planned',
        noJobs: 'No jobs in this day yet. Search job numbers above and add them.',
        noRollSize: 'No roll size',
        rollGroup: '{size}" roll',
        jobsCount: '{n} job(s)',
        needStock: '{paper}: need {need} kg · stock {have} kg',
        copyDown: 'copy ↓',
        copyDownTitle: "Copy this row's papers to every job of this size",
        midDay: 'added later',
        part: '{n}-ply part',
        partTitle: '7-ply job: made as a 3-ply part and a 4-ply part on the same day. Qty, setting, roll size and hold are the same on both parts.',

        colJob: 'Job',
        colClientBuyer: 'Client / Buyer',
        colClient: 'Client',
        colItemSpec: 'Item · Spec',
        colItem: 'Item',
        colSpec: 'Spec',
        colMeasurement: 'Measurement',
        colItemMeasurement: 'Item · Measurement',
        colOrderMade: 'Order / made',
        colOrderQty: 'Order qty',
        colFactoryMade: 'Factory made',
        colOffice: 'Office BM / Fin / Del',
        colSetting: 'Setting',
        colRoll: 'Roll"',
        colSameRoll: 'Same roll',
        sameRollTitle: 'Jobs side by side on one roll: give them the same number (1, 2...) and the same roll size. Trim is then the roll minus all their boards together.',
        colPlanQty: 'Plan qty',
        colHold: 'Hold',
        colPlanned: 'Planned',
        colMade: 'Made',
        colReason: 'Not made / short — reason',

        noPlanFor: 'No plan for {date}. Add jobs in the Plan tab first (jobs added during the day can be added there too).',
        secMade: 'A. What was made today',
        eqPlan: '= plan',
        reasonPh: 'e.g. paper not in stock',
        fillMade: 'Fill all empty "Made" = planned',
        secNotebook: 'B. Roll notebook (in the order the rolls went on the machine)',
        colNo: '#',
        colRollNo: 'Roll no',
        colPaperSize: 'Paper · size',
        colUse: 'Use',
        colPosition: 'Position',
        colUsedUp: '✗ used up',
        colBalance: 'Balance kg',
        colCore: 'Paper left on core kg',
        colStartUsed: 'Start → used',
        colApproved: 'Swap approved by / job',
        colNote: 'Note',
        inStock: '{kg} kg in stock',
        swapPh: 'if paper swapped',
        refJobPh: 'job / Tanvir no',
        rollEntryPh: 'Size + weight from the notebook, e.g. 66 946 (or roll no)',
        addRoll: 'Add roll',
        rollNotFound: 'No roll found — check size and weight',
        rollPickOne: 'More than one roll matches — click the right one',
        rollNoShort: 'roll',
        rollInList: 'already in list',
        secCheck: 'C. Check & close',
        save: 'Save notebook',
        saving: 'Saving…',
        saved: 'Saved',
        check: 'Check',
        checkFail: 'Check failed',
        closeDay: 'Close the day',
        closing: 'Closing…',
        pressCheck: 'Press Check to see expected vs used paper.',
        changed: 'Changed — press Check again.',
        reopen: 'Reopen this day (admin)',
        saveFail: 'Could not save',
        closeTitle: 'Close {date}?',
        closeMsg: 'Roll weights in stock will be lowered and the paper per job saved.',
        closeOk: 'Day closed',
        closeFail: 'Could not close',
        reopenTitle: 'Reopen {date}?',
        reopenMsg: 'Roll weights go back to what they were before this day was closed.',
        reopenBtn: 'Reopen',
        reopenOk: 'Day reopened',
        reopenFail: 'Could not reopen',
        fixFirst: 'Fix before closing:',
        totals: 'Board paper used {board} kg · expected {theory} kg + trim {trim} kg (oversize rolls {oversize} kg) · wastage {waste} · other use {other} kg · core leftover {core} kg · cores {cores} kg (8 kg each) · paper cost {cost} Tk',
        colRun: 'Run',
        colExpected: 'Expected kg',
        colTrim: 'Trim kg',
        oversizeShort: 'oversize',
        runAuto: 'run: auto',
        runFor: 'for {size}" run',
        shareLink: '+ used by several runs',
        shareTitle: 'This roll was used by the jobs of several runs (e.g. a 54" liner for the 52" and 54" jobs). Tick the runs; its kg is split by need.',
        colUsed: 'Used kg',
        colWastage: 'Wastage',
        colRunReason: 'Reason',
        total: 'Total',
        reasonRequired: 'Required: why so much / so little?',
        optional: 'optional',
        wasteHelp: 'Wastage = (used − expected − trim) ÷ expected. Green up to {ok}%, yellow up to {warn}%, red above (or below {low}%). Only a red day needs a reason; a run\'s reason is optional.',
        dayReason: 'Reason for the day\'s wastage',
        dayWastage: 'day',

        kind: { Board: 'Board (machine)', EFlute: 'E-Flute', Cutting: 'Cutting / sample', Other: 'Other' },

        msg: {
            made_missing: '{job}: made qty not entered (enter 0 if not made)',
            size_missing: '{job}: roll size missing',
            no_sqm: '{job}: the order has no measurement, expected kg is 0',
            board_wider: '{job}: board needs {need}" but roll size is {roll}" — check setting / roll size',
            row_not_found: 'Row {seq}: roll not found',
            row_returned: 'Row {seq}: roll {roll} was returned to the supplier',
            row_used_up_before: 'Row {seq}: roll {roll} was already used up in row {row}',
            row_finished_stock: 'Row {seq}: roll {roll} is already finished in stock',
            row_need_balance: 'Row {seq}: roll {roll} — tick ✗ (used up) or type the balance kg',
            row_balance_negative: 'Row {seq}: balance cannot be negative',
            row_balance_too_much: 'Row {seq}: balance {end} kg is more than the {start} kg on the roll',
            row_core_too_much: 'Row {seq}: paper left on core {core} kg is more than what was on the roll',
            row_need_position: 'Row {seq}: roll {roll} — pick the machine position',
            row_balance_below_core: 'Row {seq}: roll {roll} — balance {end} kg is less than the {core} kg core. If the roll is finished, tick ✗',
            row_no_job_size: 'Row {seq}: roll {roll} is {w}", narrower than every job made today',
            row_run_not_made: 'Row {seq}: roll {roll} — no job made today at the picked {size}" run',
            row_run_wider: 'Row {seq}: roll {roll} is {w}", narrower than the picked {size}" run',
            row_other_team: 'Row {seq}: roll {roll} belongs to {team}',
            row_position_unplanned: 'Row {seq}: {pos} is not planned for any {size}" job; kg shared by all {size}" jobs',
            row_swap: 'Row {seq}: paper swap — {used} instead of {planned}. Who approved?',
            no_rolls_for_size: 'Jobs were made at {size}" but no rolls are entered for {size}"',
            reason_needed: '{size}" run: wastage {pct}% — write the reason',
            day_reason_needed: 'Day wastage {pct}% — write the reason for the day',
            trim_high: '{size}" run: trim {pct}% (roll much wider than the boards)',
            later_closed: '{date} is already closed. Reopen it first, then close this day.',
            later_closed_reopen: '{date} is already closed. Reopen it first, then reopen this day.',
            earlier_open: '{date} has rolls entered but is not closed. Close that day first.',
            // short versions shown inside a notebook row
            not_found: 'roll not found',
            returned: 'returned to the supplier',
            used_up_before: 'already used up in row {row}',
            finished_stock: 'already finished in stock',
            need_balance: 'tick ✗ or type the balance kg',
            balance_negative: 'balance cannot be negative',
            balance_too_much: 'balance {end} kg is more than {start} kg on the roll',
            core_too_much: 'core leftover {core} kg is too much',
            need_position: 'pick the position',
            balance_below_core: 'balance {end} kg is less than the {core} kg core — tick ✗ if finished',
            no_job_size: '{w}" is narrower than every job made today',
            run_not_made: 'no job made today at {size}"',
            run_wider: 'roll is narrower than the {size}" run',
            position_unplanned: '{pos} not planned for {size}" jobs',
            swap: 'paper swap: {used} instead of {planned}',
        },
    },
    bn: {
        title: 'বোর্ড মেকিং প্ল্যান',
        today: 'আজ',
        tomorrow: 'আগামীকাল',
        closed: 'বন্ধ করা হয়েছে',
        open: 'খোলা',
        noPlanYet: 'এখনো প্ল্যান নেই',
        recentDays: 'আগের দিনগুলো…',
        dayItem: '{date} — {status} ({jobs}টি জব, {rolls}টি রোল)',
        loading: 'লোড হচ্ছে…',
        tabPlan: '১. প্ল্যান',
        tabClose: '২. দিন শেষের হিসাব',
        loadFail: 'পেজ লোড হয়নি।',
        linkConsumption: 'জব অনুযায়ী পেপার রিপোর্ট',
        linkBalance: 'পেপার ব্যালেন্স',

        searchPlaceholder: 'খুঁজুন: জব নম্বর (7838 7840) অথবা পার্টি / বায়ার / আইটেম',
        search: 'খুঁজুন',
        searching: 'খুঁজছে…',
        searchFail: 'খোঁজা যায়নি',
        showPending: 'সব বাকি জব',
        filters: 'ফিল্টার',
        fParty: 'পার্টি / বায়ার',
        fPly: 'প্লাই (সব)',
        fSize: 'রোল সাইজ',
        fFrom: 'অর্ডারের তারিখ থেকে',
        fTo: 'পর্যন্ত',
        fApply: 'দেখান',
        fClear: 'মুছুন',
        pendingTitle: 'যেসব জবের পরিমাণ বাকি আছে ({n}টি, নতুনগুলো উপরে) — টিক দিয়ে যোগ করুন',
        searchTitle: 'খোঁজার ফল ({n}টি)',
        hideList: 'লুকান',
        showList: 'দেখান',
        colCompany: 'কোম্পানি',
        colAlreadyMade: 'আগে বানানো',
        colLeft: 'বাকি',
        doneLabel: 'শেষ',
        noJobFound: 'কোনো অ্যাপ্রুভড জব পাওয়া যায়নি।',
        inPlan: 'প্ল্যানে আছে',
        addTicked: 'টিক দেওয়া লাইনগুলো {date} তারিখে যোগ করুন',
        adding: 'যোগ হচ্ছে…',
        cancel: 'বাদ দিন',
        tickOne: 'অন্তত একটি লাইনে টিক দিন',
        addedLines: '{added}টি লাইন যোগ হয়েছে',
        alreadyIn: ', {n}টি আগেই প্ল্যানে ছিল',
        addFail: 'যোগ করা যায়নি',
        notSaved: 'সেভ হয়নি',
        confirmPlan: 'প্ল্যান কনফার্ম',
        confirmedBy: 'প্ল্যান কনফার্ম করেছেন {name} · {time}',
        changedTitle: 'অন্য জায়গা থেকে বদলানো হয়েছে',
        changedMsg: 'এই পেজ খোলার পরে এই দিনের রোল খাতা অন্য জায়গা থেকে (Claude বা অন্য স্ক্রিন) বদলানো হয়েছে। নতুনটা দেখতে রিলোড করুন — এখানে লেখা কিন্তু সেভ না করা পরিবর্তন মুছে যাবে।',
        reloadBtn: 'রিলোড',
        removeTitle: 'প্ল্যান থেকে বাদ দেবেন?',
        remove: 'বাদ দিন',
        removeFail: 'বাদ দেওয়া যায়নি',
        printSheet: 'শিট প্রিন্ট',
        downloadExcel: 'এক্সেল ডাউনলোড',
        allowPopups: 'প্রিন্টের জন্য পপ-আপ চালু করুন',
        excelFail: 'এক্সেল ফাইল বানানো যায়নি',
        linesPlanned: '{lines}টি লাইন · মোট {pcs} পিস প্ল্যান',
        noJobs: 'এই দিনে এখনো কোনো জব নেই। উপরে জব নম্বর খুঁজে যোগ করুন।',
        noRollSize: 'রোল সাইজ দেওয়া নেই',
        rollGroup: '{size}" রোল',
        jobsCount: '{n}টি জব',
        needStock: '{paper}: লাগবে {need} কেজি · স্টকে {have} কেজি',
        copyDown: 'নিচে কপি ↓',
        copyDownTitle: 'এই লাইনের পেপার এই সাইজের সব জবে বসিয়ে দিন',
        midDay: 'পরে যোগ',
        part: '{n}-প্লাই অংশ',
        partTitle: '৭-প্লাই জব: একই দিনে ৩-প্লাই অংশ আর ৪-প্লাই অংশ বানানো হয়। পিস, সেটিং, রোল সাইজ আর হোল্ড দুই অংশে একই।',

        colJob: 'জব',
        colClientBuyer: 'ক্লায়েন্ট / বায়ার',
        colClient: 'ক্লায়েন্ট',
        colItemSpec: 'আইটেম · প্লাই',
        colItem: 'আইটেম',
        colSpec: 'প্লাই',
        colMeasurement: 'মাপ',
        colItemMeasurement: 'আইটেম · মাপ',
        colOrderMade: 'অর্ডার / আগে বানানো',
        colOrderQty: 'অর্ডার পিস',
        colFactoryMade: 'ফ্যাক্টরিতে বানানো',
        colOffice: 'অফিস BM / ফিনিশিং / ডেলিভারি',
        colSetting: 'সেটিং',
        colRoll: 'রোল"',
        colSameRoll: 'একই রোল',
        sameRollTitle: 'যে জবগুলো একই রোলে পাশাপাশি চলে, সেগুলোতে একই নম্বর (১, ২...) আর একই রোল সাইজ দিন। তখন ট্রিম হবে রোল থেকে সব বোর্ড একসাথে বাদ দিয়ে।',
        colPlanQty: 'প্ল্যান পিস',
        colHold: 'হোল্ড',
        colPlanned: 'প্ল্যান',
        colMade: 'বানানো হয়েছে',
        colReason: 'কম / না হওয়ার কারণ',

        noPlanFor: '{date} তারিখের কোনো প্ল্যান নেই। আগে প্ল্যান ট্যাবে জব যোগ করুন (দিনের মাঝে আসা জবও সেখানে যোগ করা যায়)।',
        secMade: 'ক. আজ কী কী বানানো হলো',
        eqPlan: '= প্ল্যান',
        reasonPh: 'যেমন: পেপার স্টকে নেই',
        fillMade: 'খালি "বানানো" ঘরে প্ল্যানের সংখ্যা বসান',
        secNotebook: 'খ. রোল খাতা (রোল যে ক্রমে মেশিনে উঠেছে)',
        colNo: 'ক্রম',
        colRollNo: 'রোল নং',
        colPaperSize: 'পেপার · সাইজ',
        colUse: 'কোথায় লেগেছে',
        colPosition: 'পজিশন',
        colUsedUp: '✗ শেষ',
        colBalance: 'ব্যালেন্স কেজি',
        colCore: 'কোরে বাকি পেপার কেজি',
        colStartUsed: 'শুরু → খরচ',
        colApproved: 'পেপার বদল কে বলেছে / জব',
        colNote: 'নোট',
        inStock: 'স্টকে {kg} কেজি',
        swapPh: 'পেপার বদলালে',
        refJobPh: 'জব / তানভীর নং',
        rollEntryPh: 'খাতা থেকে সাইজ + ওজন লিখুন, যেমন 66 946 (বা রোল নং)',
        addRoll: 'রোল যোগ',
        rollNotFound: 'রোল পাওয়া যায়নি — সাইজ আর ওজন দেখুন',
        rollPickOne: 'একাধিক রোল মিলেছে — সঠিকটায় ক্লিক করুন',
        rollNoShort: 'রোল',
        rollInList: 'আগেই লিস্টে আছে',
        secCheck: 'গ. চেক করে দিন বন্ধ করুন',
        save: 'খাতা সেভ',
        saving: 'সেভ হচ্ছে…',
        saved: 'সেভ হয়েছে',
        check: 'চেক',
        checkFail: 'চেক করা যায়নি',
        closeDay: 'দিন বন্ধ করুন',
        closing: 'বন্ধ হচ্ছে…',
        pressCheck: 'কত পেপার লাগার কথা আর কত লেগেছে দেখতে "চেক" চাপুন।',
        changed: 'পরিবর্তন হয়েছে — আবার "চেক" চাপুন।',
        reopen: 'এই দিন আবার খুলুন (অ্যাডমিন)',
        saveFail: 'সেভ করা যায়নি',
        closeTitle: '{date} বন্ধ করবেন?',
        closeMsg: 'স্টকে রোলের ওজন কমে যাবে আর প্রতিটি জবের পেপার সেভ হবে।',
        closeOk: 'দিন বন্ধ হয়েছে',
        closeFail: 'বন্ধ করা যায়নি',
        reopenTitle: '{date} আবার খুলবেন?',
        reopenMsg: 'রোলের ওজন আগের মতো ফেরত যাবে।',
        reopenBtn: 'আবার খুলুন',
        reopenOk: 'দিন আবার খোলা হয়েছে',
        reopenFail: 'আবার খোলা যায়নি',
        fixFirst: 'বন্ধ করার আগে এগুলো ঠিক করুন:',
        totals: 'বোর্ডে পেপার লেগেছে {board} কেজি · লাগার কথা {theory} কেজি + ট্রিম {trim} কেজি (ওভার রোল {oversize} কেজি) · ওয়েস্টেজ {waste} · অন্য কাজে {other} কেজি · কোরে বাকি {core} কেজি · কোর {cores} কেজি (প্রতিটা ৮ কেজি) · পেপারের দাম {cost} টাকা',
        colRun: 'রান',
        colExpected: 'লাগার কথা কেজি',
        colTrim: 'ট্রিম কেজি',
        oversizeShort: 'ওভার রোল',
        runAuto: 'রান: অটো',
        runFor: '{size}" রানে',
        shareLink: '+ কয়েক রানে লেগেছে',
        shareTitle: 'এই রোল কয়েকটা রানের জবে লেগেছে (যেমন 54" লাইনার 52" আর 54" এর জবে)। রানগুলোতে টিক দিন; কেজি প্রয়োজন মতো ভাগ হবে।',
        colUsed: 'লেগেছে কেজি',
        colWastage: 'ওয়েস্টেজ',
        colRunReason: 'কারণ',
        total: 'মোট',
        reasonRequired: 'কারণ লিখতেই হবে: এত বেশি / কম কেন?',
        optional: 'ইচ্ছা হলে লিখুন',
        wasteHelp: 'ওয়েস্টেজ = (লেগেছে − লাগার কথা − ট্রিম) ÷ লাগার কথা। {ok}% পর্যন্ত সবুজ, {warn}% পর্যন্ত হলুদ, এর বেশি (বা {low}% এর কম) হলে লাল। শুধু পুরো দিন লাল হলে কারণ লিখতে হবে; রানের কারণ ইচ্ছা হলে।',
        dayReason: 'আজকের ওয়েস্টেজের কারণ',
        dayWastage: 'পুরো দিন',

        kind: { Board: 'বোর্ড (মেশিন)', EFlute: 'ই-ফ্লুট', Cutting: 'কাটিং / স্যাম্পল', Other: 'অন্যান্য' },

        msg: {
            made_missing: '{job}: কত বানানো হয়েছে লেখা হয়নি (না হলে 0 লিখুন)',
            size_missing: '{job}: রোল সাইজ নেই',
            no_sqm: '{job}: অর্ডারে মাপ নেই, তাই লাগার কথা কেজি 0',
            board_wider: '{job}: বোর্ডে {need}" লাগে কিন্তু রোল সাইজ {roll}" — সেটিং / রোল সাইজ দেখুন',
            row_not_found: 'লাইন {seq}: রোল পাওয়া যায়নি',
            row_returned: 'লাইন {seq}: রোল {roll} সাপ্লায়ারকে ফেরত দেওয়া হয়েছে',
            row_used_up_before: 'লাইন {seq}: রোল {roll} লাইন {row}-এ আগেই শেষ হয়েছে',
            row_finished_stock: 'লাইন {seq}: রোল {roll} স্টকে আগেই শেষ দেখানো আছে',
            row_need_balance: 'লাইন {seq}: রোল {roll} — শেষ হলে ✗ দিন, না হলে ব্যালেন্স কেজি লিখুন',
            row_balance_negative: 'লাইন {seq}: ব্যালেন্স মাইনাস হতে পারে না',
            row_balance_too_much: 'লাইন {seq}: ব্যালেন্স {end} কেজি, কিন্তু রোলে ছিল {start} কেজি',
            row_core_too_much: 'লাইন {seq}: কোরে বাকি {core} কেজি রোলের ওজনের চেয়ে বেশি',
            row_need_position: 'লাইন {seq}: রোল {roll} — মেশিনের পজিশন বাছুন',
            row_balance_below_core: 'লাইন {seq}: রোল {roll} — ব্যালেন্স {end} কেজি, কিন্তু কোরের ওজনই {core} কেজি। রোল শেষ হলে ✗ দিন',
            row_no_job_size: 'লাইন {seq}: রোল {roll} {w}", আজকের সব জবের চেয়ে ছোট',
            row_run_not_made: 'লাইন {seq}: রোল {roll} — বেছে নেওয়া {size}" রানে আজ কোনো জব বানানো হয়নি',
            row_run_wider: 'লাইন {seq}: রোল {roll} {w}", বেছে নেওয়া {size}" রানের চেয়ে ছোট',
            row_other_team: 'লাইন {seq}: রোল {roll} {team}-এর',
            row_position_unplanned: 'লাইন {seq}: {size}" এর কোনো জবে {pos} প্ল্যান করা নেই; কেজি সব {size}" জবে ভাগ হবে',
            row_swap: 'লাইন {seq}: পেপার বদল — {planned} এর বদলে {used}। কে বলেছে?',
            no_rolls_for_size: '{size}" সাইজে জব বানানো হয়েছে কিন্তু {size}" এর কোনো রোল খাতায় নেই',
            reason_needed: '{size}" রান: ওয়েস্টেজ {pct}% — কারণ লিখুন',
            day_reason_needed: 'পুরো দিনের ওয়েস্টেজ {pct}% — দিনের কারণ লিখুন',
            trim_high: '{size}" রান: ট্রিম {pct}% (রোল বোর্ডের চেয়ে অনেক চওড়া)',
            later_closed: '{date} আগেই বন্ধ হয়েছে। আগে ওটা আবার খুলুন, তারপর এই দিন বন্ধ করুন।',
            later_closed_reopen: '{date} আগেই বন্ধ হয়েছে। আগে ওটা আবার খুলুন, তারপর এই দিন খুলুন।',
            earlier_open: '{date} তারিখে রোল লেখা আছে কিন্তু দিন বন্ধ হয়নি। আগে ওই দিন বন্ধ করুন।',
            not_found: 'রোল পাওয়া যায়নি',
            returned: 'সাপ্লায়ারকে ফেরত দেওয়া',
            used_up_before: 'লাইন {row}-এ আগেই শেষ',
            finished_stock: 'স্টকে আগেই শেষ',
            need_balance: '✗ দিন অথবা ব্যালেন্স কেজি লিখুন',
            balance_negative: 'ব্যালেন্স মাইনাস হতে পারে না',
            balance_too_much: 'ব্যালেন্স {end} কেজি, রোলে ছিল {start} কেজি',
            core_too_much: 'কোরে বাকি {core} কেজি বেশি হয়ে গেছে',
            need_position: 'পজিশন বাছুন',
            balance_below_core: 'ব্যালেন্স {end} কেজি, কোরই {core} কেজি — রোল শেষ হলে ✗ দিন',
            no_job_size: '{w}" আজকের সব জবের চেয়ে ছোট',
            run_not_made: 'আজ {size}" সাইজে কোনো জব নেই',
            run_wider: 'রোল {size}" রানের চেয়ে ছোট',
            position_unplanned: '{size}" জবে {pos} প্ল্যান করা নেই',
            swap: 'পেপার বদল: {planned} এর বদলে {used}',
        },
    },
};

const fill = (s, params) => String(s).replace(/\{(\w+)\}/g, (m, k) => (params && params[k] != null ? params[k] : m));

export const LangContext = createContext('en');

export function useT() {
    const lang = useContext(LangContext);
    const dict = STRINGS[lang] || STRINGS.en;
    const t = (key, params) => fill(dict[key] ?? STRINGS.en[key] ?? key, params);
    // A server message {code, params, text}: our own words when we know the code, else the server's text
    t.msg = (m) => {
        if (!m) return '';
        const s = m.code && (dict.msg[m.code] ?? STRINGS.en.msg[m.code]);
        return s ? fill(s, m.params) : (m.text || '');
    };
    t.kind = (k) => dict.kind[k] || STRINGS.en.kind[k] || k;
    t.lang = lang;
    return t;
}

const KEY = 'boardPlan.lang';
export const loadLang = () => {
    try { return localStorage.getItem(KEY) === 'bn' ? 'bn' : 'en'; } catch (_) { return 'en'; }
};
export const saveLang = (lang) => {
    try { localStorage.setItem(KEY, lang); } catch (_) { /* private window */ }
};
